import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Loader2, Mail, User, MessageSquare, Check } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const contactSchema = z.object({
  name: z
    .string()
    .min(2, "Navn skal være mindst 2 tegn")
    .max(100, "Navn må højst være 100 tegn"),
  email: z
    .string()
    .email("Indtast en gyldig e-mailadresse")
    .max(255, "E-mail må højst være 255 tegn"),
  subject: z
    .string()
    .min(1, "Vælg venligst et emne"),
  message: z
    .string()
    .min(10, "Beskeden skal være mindst 10 tegn")
    .max(2000, "Beskeden må højst være 2000 tegn"),
});

type ContactFormData = z.infer<typeof contactSchema>;

const subjects = [
  { value: "general", label: "Generel forespørgsel" },
  { value: "feedback", label: "Feedback" },
  { value: "collaboration", label: "Samarbejde" },
  { value: "other", label: "Andet" },
];

const Contact = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const formSection = useScrollAnimation();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    setIsLoading(true);
    try {
      // Get subject label
      const subjectLabel = subjects.find(s => s.value === data.subject)?.label || data.subject;

      // Insert into database
      const { error: dbError } = await supabase
        .from("contact_inquiries")
        .insert({
          name: data.name.trim(),
          email: data.email.trim().toLowerCase(),
          subject: subjectLabel,
          message: data.message.trim(),
        });

      if (dbError) {
        throw dbError;
      }

      // Send notification email
      const { error: emailError } = await supabase.functions.invoke(
        "send-contact-notification",
        {
          body: {
            name: data.name.trim(),
            email: data.email.trim().toLowerCase(),
            subject: subjectLabel,
            message: data.message.trim(),
          },
        }
      );

      if (emailError) {
        console.error("Error sending notification:", emailError);
      }

      setIsSubmitted(true);
    } catch (error: any) {
      console.error("Error submitting contact form:", error);
      toast({
        title: "Der opstod en fejl",
        description: "Prøv venligst igen senere.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Kontakt os | MySabbat"
        description="Har du spørgsmål eller feedback? Kontakt MySabbat teamet. Vi svarer hurtigst muligt."
        path="/kontakt"
      />
      <Header />

      <main className="flex-1 container py-12 md:py-20">
        <div className="max-w-xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Tilbage til forsiden
          </Link>

          <div ref={formSection.ref}>
            <h1
              className={`scroll-animate ${formSection.isVisible ? "visible" : ""} text-3xl md:text-4xl font-semibold text-foreground mb-4`}
            >
              Kontakt os
            </h1>
            <p
              className={`scroll-animate delay-1 ${formSection.isVisible ? "visible" : ""} text-muted-foreground mb-10`}
            >
              Har du spørgsmål, feedback eller idéer? Vi vil meget gerne høre fra dig.
            </p>

            {!isSubmitted ? (
              <div
                className={`scroll-animate delay-2 ${formSection.isVisible ? "visible" : ""} bg-card rounded-2xl p-6 md:p-8 shadow-sm border border-border/40`}
              >
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Navn</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                placeholder="Dit navn"
                                className="pl-11 h-12 rounded-xl border-border/60 bg-background focus:border-primary"
                                disabled={isLoading}
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>E-mail</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              <Input
                                type="email"
                                placeholder="din@email.dk"
                                className="pl-11 h-12 rounded-xl border-border/60 bg-background focus:border-primary"
                                disabled={isLoading}
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="subject"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Emne</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            disabled={isLoading}
                          >
                            <FormControl>
                              <SelectTrigger className="h-12 rounded-xl border-border/60 bg-background focus:border-primary">
                                <SelectValue placeholder="Vælg et emne" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-card border-border">
                              {subjects.map((subject) => (
                                <SelectItem key={subject.value} value={subject.value}>
                                  {subject.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Besked</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <MessageSquare className="absolute left-4 top-4 w-4 h-4 text-muted-foreground" />
                              <Textarea
                                placeholder="Skriv din besked her..."
                                className="pl-11 min-h-[150px] rounded-xl border-border/60 bg-background focus:border-primary resize-none"
                                disabled={isLoading}
                                {...field}
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                          <p className="text-xs text-muted-foreground mt-1">
                            {field.value.length}/2000 tegn
                          </p>
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isLoading}
                      className="w-full h-12 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 bg-accent hover:bg-accent/90 text-accent-foreground"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send besked
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              </div>
            ) : (
              <div className="opacity-0 animate-scale-in bg-card rounded-2xl p-8 shadow-sm border border-border/40 text-center">
                <div className="w-14 h-14 rounded-full bg-ms-sage flex items-center justify-center mx-auto mb-5">
                  <Check className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Tak for din henvendelse!
                </h2>
                <p className="text-muted-foreground mb-6">
                  Vi vender tilbage hurtigst muligt.
                </p>
                <Button asChild variant="outline" className="rounded-xl">
                  <Link to="/">Tilbage til forsiden</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default Contact;
